import { useState } from 'react';
import { AudioEngineError } from '../audio/errors';
import { LibraryPanel } from '../components/library/LibraryPanel';
import type { ImportSongRequest } from '../components/library/LibraryPanel';
import { MixerPanel } from '../components/mixer/MixerPanel';
import { PlayerPanel } from '../components/player/PlayerPanel';
import { StatusBar } from '../components/status/StatusBar';
import { useAudioEngine } from '../hooks/useAudioEngine';
import type { DecodedTrack } from '../types/audio';

interface LoadedSong {
  title: string;
  artist: string;
  bpm: string;
  key: string;
  tracks: DecodedTrack[];
  durationWarning: string | null;
}

export function App() {
  const { engine, snapshot, execute } = useAudioEngine();
  const [search, setSearch] = useState('');
  const [song, setSong] = useState<LoadedSong | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [uiError, setUiError] = useState<string | null>(null);
  const tracks = engine.getTracks();
  const filteredTracks = tracks.filter((track) =>
    track.name.toLowerCase().includes(search.toLowerCase()),
  );

  async function importSong(request: ImportSongRequest) {
    if (!engine) return;
    setIsImporting(true);
    setUiError(null);
    try {
      const decoded = await engine.decodeTracks(
        request.tracks.map((track) => ({
          id: crypto.randomUUID(),
          name: track.name,
          role: track.role,
          volume: 0.8,
          muted: false,
          solo: false,
          pan: 0,
          file: track.file,
        })),
      );
      engine.loadTracks(decoded);
      const durations = decoded.map((track) => track.buffer.duration);
      const difference = Math.max(...durations) - Math.min(...durations);
      setSong({
        title: request.title.trim() || 'Sin título',
        artist: request.artist.trim(),
        bpm: request.bpm.trim(),
        key: request.key.trim(),
        tracks: decoded,
        durationWarning:
          difference > 0.1
            ? `Hay ${difference.toFixed(1)} s de diferencia entre la pista más corta y la más larga.`
            : null,
      });
    } catch (error) {
      setUiError(
        error instanceof AudioEngineError || error instanceof Error
          ? error.message
          : 'No se pudo cargar la canción.',
      );
    } finally {
      setIsImporting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#080b10] text-slate-100">
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-white/8 px-4 lg:px-6">
        <div className="flex items-center gap-3">
          <div className="brand-mark" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div>
            <p className="text-sm font-bold tracking-[0.2em] text-white">PRIME</p>
            <p className="text-[10px] font-medium tracking-[0.18em] text-slate-500">MULTITRACKS</p>
          </div>
        </div>
        <div className="hidden items-center gap-2 text-xs text-slate-400 sm:flex">
          <span
            className={`status-dot ${snapshot.status === 'error' ? 'bg-rose-400' : 'bg-emerald-400'}`}
          />
          {snapshot.status === 'playing' ? 'Reproduciendo' : 'Motor disponible'}
        </div>
      </header>

      <main className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)]">
        <LibraryPanel
          search={search}
          onSearch={setSearch}
          song={
            song ? { title: song.title, artist: song.artist, trackCount: song.tracks.length } : null
          }
          isImporting={isImporting}
          onImport={importSong}
        />
        <section className="flex min-h-0 flex-col overflow-hidden">
          <PlayerPanel
            song={song}
            snapshot={snapshot}
            onPlay={() =>
              execute((audio) => audio.play()).catch((error: unknown) => setUiError(String(error)))
            }
            onPause={() => engine?.pause()}
            onStop={() => engine?.stop()}
            onSeek={(seconds) =>
              execute((audio) => audio.seek(seconds)).catch((error: unknown) =>
                setUiError(String(error)),
              )
            }
          />
          <MixerPanel
            tracks={filteredTracks}
            getLevel={(trackId) => engine?.getTrackLevel(trackId) ?? 0}
            onVolume={(trackId, volume) => engine?.setTrackVolume(trackId, volume)}
            onMuted={(trackId, muted) => engine?.setTrackMuted(trackId, muted)}
            onSolo={(trackId, solo) => engine?.setTrackSolo(trackId, solo)}
            onPan={(trackId, pan) => engine?.setTrackPan(trackId, pan)}
          />
        </section>
      </main>

      <StatusBar
        snapshot={snapshot}
        onOutputMode={(mode) =>
          execute((audio) => audio.setOutputMode(mode)).catch((error: unknown) =>
            setUiError(String(error)),
          )
        }
        onMasterVolume={(volume) => engine?.setMasterVolume(volume)}
        message={uiError ?? song?.durationWarning ?? snapshot.error ?? snapshot.warning}
      />
    </div>
  );
}
